import { useCallback, useRef, useState } from 'react'
import useAPI from '~/prix/react/hooks/api'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import viewListDefinition from '~/packages/general/view/list/viewList.definition'
import { ViewListItem } from '~/packages/general/view/list/viewList.typing'

const useVariableListByCategory = () => {
  const PAGESIZE = 500

  const { callAction } = useActionFunction(viewListDefinition, {
    staleWhileRevalidate: false,
  })
  const currentAbort = useRef<(() => void) | null>(null)
  const currentQueryKey = useRef<string | null>(null)
  const latestRequestId = useRef(0)

  const { isLoading } = useAPI()

  const [variables, setVariables] = useState<ViewListItem[]>([])

  const [category, setCategory] = useState<{
    key: string
    title: string
    sectorKey: string
  } | null>(null)
  const [totalByCategory, setTotalByCategory] = useState(0)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [numberOfVariables, setNumberOfVariables] = useState(0)
  const [pagesLeft, setPagesLeft] = useState(0)

  const [isLoadingVariables, setIsLoadingVariables] = useState(false)

  const abortCurrentRequest = useCallback(() => {
    currentAbort.current?.()
    currentAbort.current = null
  }, [])

  const fetchVariables = useCallback(
    async (
      startIndex: number,
      category: {
        key: string
        title: string
        sectorKey: string
      } | null,
      orderBy?: 'title' | 'relevance',
      pageSize: number = PAGESIZE,
      search: string = '',
      clear?: boolean,
    ) => {
      const queryKey = JSON.stringify({
        category: category?.key ?? null,
        orderBy: orderBy || 'relevance',
        pageSize,
        search,
      })
      const shouldClear = clear || startIndex === 0 || currentQueryKey.current !== queryKey
      const requestId = latestRequestId.current + 1

      latestRequestId.current = requestId
      currentQueryKey.current = queryKey

      abortCurrentRequest()
      setCategory(category)
      if (shouldClear) {
        setVariables([])
      }
      setIsLoadingVariables(true)

      try {
        const endIndex = startIndex + pageSize
        const { resultPromise, abort } = callAction({
          search,
          category: category?.key,
          startIndex,
          endIndex,
          orderBy: orderBy || undefined,
        })
        currentAbort.current = abort
        const result = await resultPromise

        if (requestId !== latestRequestId.current) {
          return
        }

        const items = result.items || []

        setNumberOfVariables(result.total || 0)
        setCurrentPage(startIndex === 0 ? 1 : startIndex / pageSize + 1)
        setPagesLeft(result?.pagesLeft && result.pagesLeft > 0 ? result.pagesLeft : 0)
        setVariables(prev => {
          if (shouldClear) {
            return items
          }

          if (startIndex > prev.length) {
            return prev
          }

          const next = [...prev]
          items.forEach((item, index) => {
            next[startIndex + index] = item
          })
          return next
        })
        setTotalByCategory(result.variablesCount || 0)
        setTotalPages(Math.ceil((result.variablesCount || 1) / pageSize))
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Erro buscando variáveis:', error)
        }
      } finally {
        if (requestId === latestRequestId.current) {
          setIsLoadingVariables(false)
        }
      }
    },
    [abortCurrentRequest, callAction],
  )

  const someIsLoading = isLoading || isLoadingVariables

  return {
    variables,
    isLoading: someIsLoading,
    fetchVariables,
    numberOfVariables,
    selectedCategory: category,
    totalByCategory,
    totalPages,
    currentPage,
    pagesLeft,
  }
}

export default useVariableListByCategory
